import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Subject } from 'rxjs';
import { TimeRecordStatus } from '../entities/TimeRecordStatus';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { EqualFilter, InFilter } from '../baseServices/filter.interface';
import { Check, EntityHelper } from '../utils/entity-helper';
import { TimeRecordStatusNumber } from '../constant/time-record-constants';

@Injectable({
  providedIn: 'root',
})
export class TimeRecordStatusService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<TimeRecordStatus>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(timeRecordStatus: TimeRecordStatus, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: timeRecordStatus, who: who, what: what });
  }

  getEmitChangeCallback(timeRecordStatus: TimeRecordStatus, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTimeRecordStatus + '_' + timeRecordStatus.RecordId,
      fn: this.emitChange.bind(this, timeRecordStatus, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.TimeRecordStatus], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getTimeRecordStatusByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecordStatus> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<TimeRecordStatus>(StoreName.TimeRecordStatus, onlineId, TimeRecordStatus, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as TimeRecordStatus;

    if (this.checkIsTimeRecordStatusSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getTimeRecordStatusByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecordStatus> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<TimeRecordStatus>(StoreName.TimeRecordStatus, recordId, TimeRecordStatus, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as TimeRecordStatus;

    if (this.checkIsTimeRecordStatusSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllTimeRecordStatuses(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecordStatus[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<TimeRecordStatus>(StoreName.TimeRecordStatus, filter, TimeRecordStatus, transaction);

    let records = result as TimeRecordStatus[];

    if (!includeDeleted) {
      records = records.filter(statusLookup => !this.checkIsTimeRecordStatusSoftDeleted(statusLookup));
    }

    return records;
  }

  async getTimeRecordStatusesByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecordStatus[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<TimeRecordStatus>(StoreName.FIPSState, filter, TimeRecordStatus, transaction);

    let records = result as TimeRecordStatus[];

    if (!includeDeleted) {
      records = result.filter(statusLookup => !this.checkIsTimeRecordStatusSoftDeleted(statusLookup));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createTimeRecordStatus(who: DataChangeEmitWho, recordToCreate: TimeRecordStatus, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateTimeRecordStatusCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.TimeRecordStatus, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTimeRecordStatusByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateTimeRecordStatus(
    who: DataChangeEmitWho,
    recordToUpdate: TimeRecordStatus,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecordStatus | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getTimeRecordStatusByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateTimeRecordStatusUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecordStatus>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeRecordStatus>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateTimeRecordStatus(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.TimeRecordStatus, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTimeRecordStatusByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateTimeRecordStatus(timeRecordStatus: TimeRecordStatus, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateTimeRecordStatusCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateTimeRecordStatusUpdate(timeRecordStatus: TimeRecordStatus, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertTimeRecordStatusFromSync(who: DataChangeEmitWho, recordToUpsert: TimeRecordStatus, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTimeRecordStatusByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTimeRecordStatus(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTimeRecordStatus(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsTimeRecordStatusSoftDeleted(record: TimeRecordStatus): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: TimeRecordStatus, b: TimeRecordStatus): boolean {
    return a.Name === b.Name && a.Number === b.Number;
  }

  // #endregion
  // region Lookup

  async getTimeRecordStatusByNumber(number: TimeRecordStatusNumber, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecordStatus> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filterData: EqualFilter = {
      equals: {
        field: 'Number',
        searchString: number,
        caseInsensitive: true,
      },
    };

    const result = await this.getAllTimeRecordStatuses(filterData, transaction, includeDeleted);

    if (result.length > 0) {
      const record = result[0];

      if (this.checkIsTimeRecordStatusSoftDeleted(record) && !includeDeleted) {
        throw new RecordSoftDeletedException(number);
      }

      return record;
    } else {
      throw new RecordNotFoundException(number);
    }
  }

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
